import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, Loader2, X, Star, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { metadataTemplatesApi, uploadToS3, queryKeys, type MetadataTemplate, type MetadataTemplateInput } from '@/lib/api';

const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

const VARS = '{team1} {team2} {teams} {sheet} {event} {date}  ·  fallback: {teams|Curling}';

interface FormState {
  name: string;
  titleTemplate: string;
  descriptionTemplate: string;
  isDefault: boolean;
  thumbnailS3Key: string | null;
  thumbnailMime: string | null;
  thumbnailPreview: string | null; // existing url or object url
}

function TemplateModal({ facilityId, initial, onClose }: { facilityId: string; initial: MetadataTemplate | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({
    name: initial?.name ?? '',
    titleTemplate: initial?.titleTemplate ?? '',
    descriptionTemplate: initial?.descriptionTemplate ?? '',
    isDefault: initial?.isDefault ?? false,
    thumbnailS3Key: null, // only set when a NEW file is uploaded; null on edit = keep existing unless cleared
    thumbnailMime: null,
    thumbnailPreview: initial?.thumbnailUrl ?? null,
  });
  const [keepExistingThumb, setKeepExistingThumb] = useState(!!initial?.hasThumbnail);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload: MetadataTemplateInput = {
        name: form.name.trim(),
        titleTemplate: form.titleTemplate,
        descriptionTemplate: form.descriptionTemplate,
        isDefault: form.isDefault,
      };
      // Thumbnail semantics: if a new file was uploaded → send its key. If the user
      // cleared it → send null. If untouched on edit → omit so the server keeps it.
      if (form.thumbnailS3Key) { payload.thumbnailS3Key = form.thumbnailS3Key; payload.thumbnailMime = form.thumbnailMime; }
      else if (!keepExistingThumb) { payload.thumbnailS3Key = null; payload.thumbnailMime = null; }
      return initial
        ? metadataTemplatesApi.update(facilityId, initial.id, payload)
        : metadataTemplatesApi.create(facilityId, payload);
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: queryKeys.metadataTemplates.list(facilityId) }); onClose(); },
    onError: (e: any) => setErr(e?.message || 'Save failed'),
  });

  const onFile = async (file: File) => {
    setErr(null);
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) { setErr('Thumbnail must be PNG or JPEG'); return; }
    if (file.size > 2 * 1024 * 1024) { setErr('Thumbnail must be ≤ 2 MB'); return; }
    setUploading(true);
    try {
      const presign = await metadataTemplatesApi.thumbnailPresign(facilityId, { filename: file.name, contentType: file.type, size: file.size });
      await uploadToS3({ url: presign.uploadUrl, fields: presign.fields }, file);
      setForm((f) => ({ ...f, thumbnailS3Key: presign.key, thumbnailMime: presign.contentType, thumbnailPreview: URL.createObjectURL(file) }));
      setKeepExistingThumb(true);
    } catch (e: any) {
      setErr(e?.message || 'Thumbnail upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clearThumb = () => {
    setForm((f) => ({ ...f, thumbnailS3Key: null, thumbnailMime: null, thumbnailPreview: null }));
    setKeepExistingThumb(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{initial ? 'Edit template' : 'New template'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className={labelCls}>Template name</label>
            <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. League Night, Playoffs" />
          </div>
          <div>
            <label className={labelCls}>Title template</label>
            <input className={inputCls} maxLength={200} value={form.titleTemplate} onChange={(e) => setForm({ ...form, titleTemplate: e.target.value })} placeholder="{teams} — {event} (Sheet {sheet})" />
          </div>
          <div>
            <label className={labelCls}>Description template</label>
            <textarea className={inputCls} rows={4} maxLength={5000} value={form.descriptionTemplate} onChange={(e) => setForm({ ...form, descriptionTemplate: e.target.value })} placeholder="Live from {event} — {date}" />
          </div>
          <p className="text-[11px] text-gray-400">Variables: {VARS}</p>

          <div>
            <label className={labelCls}>Thumbnail (PNG/JPEG, ≤ 2 MB, 1280×720)</label>
            <div className="flex items-center gap-3">
              {form.thumbnailPreview ? (
                <img src={form.thumbnailPreview} alt="thumbnail" className="h-16 w-28 rounded border border-gray-200 object-cover dark:border-gray-700" />
              ) : (
                <div className="flex h-16 w-28 items-center justify-center rounded border border-dashed border-gray-300 text-gray-300 dark:border-gray-600"><ImageIcon className="h-5 w-5" /></div>
              )}
              <div className="flex flex-col gap-1">
                <label className="cursor-pointer rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
                  {uploading ? <span className="flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</span> : 'Upload image'}
                  <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={uploading}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }} />
                </label>
                {form.thumbnailPreview && (
                  <button onClick={clearThumb} className="text-left text-xs text-red-600 hover:underline dark:text-red-400">Remove</button>
                )}
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
            Set as default template for this facility
          </label>

          {err && (
            <div className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending || uploading || form.name.trim().length === 0}
              className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MetadataTemplatesManager({ facilityId }: { facilityId: string }) {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<{ open: boolean; initial: MetadataTemplate | null }>({ open: false, initial: null });

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.metadataTemplates.list(facilityId),
    queryFn: () => metadataTemplatesApi.list(facilityId),
  });

  const del = useMutation({
    mutationFn: (id: number) => metadataTemplatesApi.delete(facilityId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.metadataTemplates.list(facilityId) }),
  });

  const templates = data?.templates ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Metadata templates</h3>
        <button onClick={() => setModal({ open: true, initial: null })}
          className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700">
          <Plus className="h-3.5 w-3.5" /> New template
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : templates.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-xs text-gray-400 dark:border-gray-700">No templates yet. Create one to apply title, description &amp; thumbnail to live streams.</p>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
              {t.thumbnailUrl
                ? <img src={t.thumbnailUrl} alt="" className="h-10 w-16 shrink-0 rounded object-cover" />
                : <div className="flex h-10 w-16 shrink-0 items-center justify-center rounded bg-gray-100 text-gray-300 dark:bg-gray-800"><ImageIcon className="h-4 w-4" /></div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{t.name}</span>
                  {t.isDefault && <span className="flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"><Star className="h-3 w-3" /> default</span>}
                </div>
                <p className="truncate text-xs text-gray-400">{t.titleTemplate || '(no title)'}</p>
              </div>
              <button onClick={() => setModal({ open: true, initial: t })} className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => { if (window.confirm(`Delete template "${t.name}"?`)) del.mutate(t.id); }} className="rounded-md border border-gray-300 p-1.5 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
            </div>
          ))}
        </div>
      )}

      {modal.open && <TemplateModal facilityId={facilityId} initial={modal.initial} onClose={() => setModal({ open: false, initial: null })} />}
    </div>
  );
}
